/**
 * Zoom control: six clickable bars of increasing height (one per
 * `ZOOM_LEVELS` entry) plus a visually-hidden antd `Slider` that carries
 * keyboard handling and ARIA. A spike established the antd slider rail is a
 * single element, so skinning it cannot produce six discrete bars — hence the
 * custom bars for pointer users and the real slider for everyone else. The
 * hidden wrapper is clip-rect, not `display: none`, so the slider stays
 * focusable and `role="slider"` stays in the tree.
 */
import { Slider } from "antd"
import { map } from "lodash"
import { memo } from "react"
import styled from "styled-components"

import {
  describeZoomLevel,
  STRIP_ZOOM_INDEX,
  ZOOM_LEVELS,
} from "./artworkWallHelpers"
import { WALL_ACCENT, WALL_TEXT_MUTED } from "./artworkWallTheme"

interface ZoomControlProps {
  zoomIndex: number
  onChange: (zoomIndex: number) => void
}

const BAR_MIN_HEIGHT = 8
const BAR_STEP = 4

const SBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: ${BAR_MIN_HEIGHT + BAR_STEP * (ZOOM_LEVELS.length - 1)}px;
`

const SBar = styled.button<{ $height: number; $active: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: block;
  width: 10px;
  height: ${({ $height }) => $height}px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? WALL_ACCENT : WALL_TEXT_MUTED)};
  transition: background 120ms ease;

  &:hover {
    background: ${WALL_ACCENT};
  }
`

const SVisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`

const SRoot = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const ZoomControl = ({ zoomIndex, onChange }: ZoomControlProps) => (
  <SRoot data-testid="zoom-control">
    <SBars aria-hidden="true" data-testid="zoom-bars">
      {map(ZOOM_LEVELS, (_, index) => (
        <SBar
          key={index}
          type="button"
          tabIndex={-1}
          $height={BAR_MIN_HEIGHT + BAR_STEP * index}
          $active={index <= zoomIndex}
          data-testid={`zoom-bar-${index}`}
          data-active={index === zoomIndex || undefined}
          title={describeZoomLevel(index)}
          onClick={() => onChange(index)}
        />
      ))}
    </SBars>
    <SVisuallyHidden>
      <Slider
        min={0}
        max={STRIP_ZOOM_INDEX}
        step={1}
        value={zoomIndex}
        onChange={onChange}
        tooltip={{ open: false }}
        ariaLabelForHandle="Zoom level"
        ariaValueTextFormatterForHandle={describeZoomLevel}
      />
    </SVisuallyHidden>
  </SRoot>
)

export default memo(ZoomControl)
